import CollapsibleView from "@components/molecules/CollapsibleView";
import { PersonalizationProps } from "./types";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { useEnablePersonalisationFont } from "@redux/slices/globalSlice";
import { dynamicMapToRowType, generateYupSchema, onChangeValue } from "./utils";
import { useTranslation } from "react-i18next";
import { Input, VDBText } from "@components/atoms";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { RowType } from "@components/molecules/DynamicForm/types";
import React, { useCallback, useContext, useEffect, useRef } from "react";
import CustomSelect from "@components/atoms/CustomSelect/CustomSelect";
import { SpaceType } from "@components/atoms/Spacer/types";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { JewelryConfiguratorContext } from "../Context/JewelryConfiguratorContext";
import { DeviceEventEmitter } from "react-native";
import { View } from "react-native";
import VDBIcon, { IconsType, IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import useEngraveFonts from "../../../../hooks/useEngraveFonts";

const Personalization = ({
  title,
  sub_options,
  engravable,
  disabled = false,
  onMultilineFocusChange,
  value,
  onChange,
  embedded = false,
  optional = false,
  compact = false,
}: PersonalizationProps) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const { t } = useTranslation();
  const typography = useTypographyStore();
  const enablePersonalisationFont = useEnablePersonalisationFont();
  const cursorPosition = useRef(0);
  const engraveTextInputRef = useRef(null);
  const configuratorContext = useContext(JewelryConfiguratorContext);
  const isControlled = typeof onChange === "function";
  const personalization = isControlled
    ? value ?? {}
    : configuratorContext?.personalization ?? {};
  const setPersonalization = isControlled
    ? onChange
    : configuratorContext?.setPersonalization ?? (() => {});
  const setHasPersonalizationError = optional
    ? () => {}
    : configuratorContext?.setHasPersonalizationError ?? (() => {});
  const requiredSymbol = optional ? "" : "*";

  let filteredFields = Object.values(sub_options ?? {})
    .filter((field) => field.price_modes.includes(userInfo.getUserPriceMode()))
    .sort((a, b) => a.order - b.order)
    .map((field) => dynamicMapToRowType<RowType>(field) as RowType);

  if (!engravable) {
    filteredFields = filteredFields.filter((field) => !field.is_Engravable);
  }
  if (optional) {
    filteredFields = filteredFields.map((field) => ({
      ...field,
      validations: field.validations
        ? { ...field.validations, required: false }
        : field.validations,
    }));
  }

  const schema = generateYupSchema(filteredFields, t);

  const prevPersonalizationRef = useRef(personalization);
  useEffect(() => {
    if (!personalization && prevPersonalizationRef.current) {
      filteredFields.forEach((field) => setValue(field.name, ""));
    }
    prevPersonalizationRef.current = personalization;
    Object.entries(personalization ?? []).forEach(
      ([key, personalizationValue]) => {
        setValue(key, personalizationValue);
      }
    );
  }, [personalization]);

  const {
    control,
    formState: { errors },
    setError,
    getValues,
    clearErrors,
    setValue,
    trigger,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
  });

  const watchedEngraveFont = watch("engrave_font");

  const { getFontFamilyForStyle, loadFont } = useEngraveFonts();

  useEffect(() => {
    if (enablePersonalisationFont && watchedEngraveFont) {
      loadFont(watchedEngraveFont);
    }
  }, [watchedEngraveFont, loadFont, enablePersonalisationFont]);

  // Only apply font styling when the feature switch is enabled
  const engraveFontFamily =
    enablePersonalisationFont && watchedEngraveFont
      ? getFontFamilyForStyle(watchedEngraveFont)
      : undefined;

  const setPersonalizationValues = (values: any) => {
    setPersonalization(values);
  };

  const addSymbol = (symbolName: string) => {
    let symbol = "";
    switch (symbolName) {
      case "heart":
        symbol = "♡";
        break;
      case "two_hearts":
        symbol = "💕";
        break;
      case "infinity":
        symbol = "∞";
        break;
      default:
        symbol = "";
        break;
    }
    const currentText = getValues("engrave_text") || "";
    const newText =
      currentText.slice(0, cursorPosition.current) +
      symbol +
      currentText.slice(cursorPosition.current);
    const engraveTextField = filteredFields.find(
      (field) => field.name === "engrave_text"
    );
    const maxLength =
      engraveTextField?.validations?.maxLength ||
      engraveTextField?.characterLimit;

    if (maxLength && newText.length > maxLength) {
      return;
    }

    setValue("engrave_text", newText);
    const newCursorPosition = cursorPosition.current + symbol.length;
    cursorPosition.current = newCursorPosition;

    setTimeout(() => {
      if (engraveTextInputRef.current) {
        engraveTextInputRef.current.setSelection(
          newCursorPosition,
          newCursorPosition
        );
      }
    }, 0);

    clearErrors("engrave_text");
    trigger("engrave_text");

    setErrors(getValues());
    setPersonalizationValues(getValues());
  };

  const setErrors = useCallback(
    (values: any) => {
      const engraveText = values.engrave_text;
      const engraveFont = values.engrave_font;

      const hasTextButNoFont =
        engraveText &&
        engraveText.trim().length > 0 &&
        (!engraveFont || engraveFont.trim().length === 0);

      const engraveTextField = filteredFields.find(
        (field) => field.name === "engrave_text"
      );
      const engraveFontField = filteredFields.find(
        (field) => field.name === "engrave_font"
      );
      const isEngraveTextRequired = engraveTextField?.validations?.required;

      const hasFontButNoText =
        engraveFont &&
        engraveFont.trim().length > 0 &&
        (!engraveText || engraveText.trim().length === 0) &&
        isEngraveTextRequired;

      if (hasTextButNoFont && engraveFontField) {
        setError("engrave_font", {
          type: "manual",
          message: t(engraveFontField.validations?.errorMessage ?? ""),
        });
      } else {
        clearErrors("engrave_font");
      }

      if (hasFontButNoText && engraveTextField) {
        setError("engrave_text", {
          type: "manual",
          message: t(engraveTextField.validations?.errorMessage ?? ""),
        });
      } else {
        clearErrors("engrave_text");
      }

      setHasPersonalizationError(
        optional ? false : hasTextButNoFont || hasFontButNoText
      );
    },
    [
      filteredFields,
      setError,
      clearErrors,
      t,
      setHasPersonalizationError,
      optional,
    ]
  );

  const renderField = (field: RowType) => {
    const commonProps = {
      control,
      name: field.name,
      render: ({ field: { onChange: fieldOnChange, value: fieldValue } }) => {
        const label = `${t(field.label)}${
          field?.validations?.required ? requiredSymbol : ""
        }`;

        const error = errors?.[field.name]?.message as string;
        const fieldTitle = embedded ? "" : t(label);
        switch (field?.type) {
          case "dropdown":
            const isEngraveFontField = field.name === "engrave_font";
            return (
              <>
                <CustomSelect
                  title={fieldTitle}
                  defaultValue={fieldValue}
                  options={field.options ?? []}
                  onSelect={async (_value) => {
                    await onChangeValue(field, _value, fieldOnChange);
                    setErrors(getValues());
                    if (field.name === "engrave_font") {
                      const engraveText = getValues("engrave_text");
                      if (engraveText && engraveText.trim().length > 0) {
                        trigger("engrave_text");
                      }
                    }
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  showError={field.name in errors && error?.trim() !== ""}
                  errorMessage={error}
                  disabled={disabled}
                  useFontStyle={isEngraveFontField}
                  compact={compact}
                />
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            );
          case "textarea":
            return embedded ? (
              <>
                <CompactEmbeddedInput
                  value={fieldValue}
                  onChangeText={(val) => {
                    onChangeValue(field, val, fieldOnChange);
                    clearErrors(field.name);
                  }}
                  onFocus={() => {
                    onMultilineFocusChange?.(true);
                  }}
                  onBlur={() => {
                    onMultilineFocusChange?.(false);
                    setErrors(getValues());
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  editable={!disabled}
                  keyboardType="default"
                />
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            ) : (
              <>
                <Input
                  inputLabel={fieldTitle}
                  onChangeText={(val) => {
                    onChangeValue(field, val, fieldOnChange);
                    clearErrors(field.name);
                  }}
                  value={fieldValue}
                  keyboardType="default"
                  height="96"
                  multiline
                  error={error}
                  onFocus={() => {
                    onMultilineFocusChange?.(true);
                  }}
                  onBlur={() => {
                    onMultilineFocusChange?.(false);
                    setErrors(getValues());
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  disabled={disabled}
                />
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            );
          case "grid":
            return (
              <GridContainer>
                {field && field?.options?.length
                  ? field?.options.map((icon) => (
                      <View>
                        <StyledIcon
                          compact={compact}
                          onPress={() =>
                            addSymbol(icon?.value || icon?.name || "heart")
                          }
                        >
                          <VDBIcon
                            name={icon?.icon?.data || VDBIcons.HEART}
                            uri={icon?.icon?.data}
                            size={24}
                            color={theme["core-06"]}
                            variant={
                              icon?.icon?.type === IconsType.img_url
                                ? IconsVariant.CustomUrl
                                : IconsVariant.VDBCustom
                            }
                          />
                        </StyledIcon>
                      </View>
                    ))
                  : null}
              </GridContainer>
            );
          default:
            const isEngraveTextField = field.name === "engrave_text";

            if (embedded && (!isEngraveTextField || compact)) {
              return (
                <>
                  <CompactEmbeddedInput
                    ref={isEngraveTextField ? engraveTextInputRef : undefined}
                    value={fieldValue}
                    onChangeText={(val) => {
                      onChangeValue(field, val, fieldOnChange);
                      clearErrors(field.name);
                      setPersonalizationValues(getValues());
                    }}
                    onSelectionChange={
                      isEngraveTextField
                        ? (event) => {
                            cursorPosition.current =
                              event.nativeEvent.selection.start;
                          }
                        : undefined
                    }
                    onBlur={() => {
                      trigger(field.name);
                      setErrors(getValues());
                      setPersonalizationValues(getValues());
                      if (field?.reinitialize_index_on_change) {
                        DeviceEventEmitter.emit("RESET_GALLERY");
                      }
                    }}
                    editable={!disabled}
                    {...(isEngraveTextField &&
                      engraveFontFamily && { fontFamily: engraveFontFamily })}
                  />
                  {isEngraveTextField &&
                    field.characterLimit &&
                    field.characterLimitText && (
                      <VDBText
                        color={theme["core-06"]}
                        type={typography["Minor-R"]}
                      >
                        {t(field.characterLimitText, {
                          limit: field.characterLimit,
                          remainingLimit:
                            Number(field.characterLimit) -
                            (getValues()[field.name]?.length ?? 0),
                        })}
                      </VDBText>
                    )}
                  {field.note_text?.trim() && (
                    <VDBText
                      color={theme["core-06"]}
                      type={typography["Minor-R"]}
                    >
                      {t(field.note_text)}
                    </VDBText>
                  )}
                </>
              );
            }

            return (
              <>
                <Input
                  inputLabel={fieldTitle}
                  onChangeText={(val) => {
                    onChangeValue(field, val, fieldOnChange);
                    clearErrors(field.name);
                    setPersonalizationValues(getValues());
                  }}
                  onSelectionChange={
                    isEngraveTextField
                      ? (event) => {
                          cursorPosition.current =
                            event.nativeEvent.selection.start;
                        }
                      : undefined
                  }
                  value={fieldValue}
                  error={error}
                  onBlur={() => {
                    trigger(field.name);
                    setErrors(getValues());
                    setPersonalizationValues(getValues());
                    if (field?.reinitialize_index_on_change) {
                      DeviceEventEmitter.emit("RESET_GALLERY");
                    }
                  }}
                  disabled={disabled}
                  {...(isEngraveTextField &&
                    engraveFontFamily && { fontFamily: engraveFontFamily })}
                />
                {field.characterLimit && field.characterLimitText && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.characterLimitText, {
                      limit: field.characterLimit,
                      remainingLimit:
                        Number(field.characterLimit) -
                        (getValues()[field.name]?.length ?? 0),
                    })}
                  </VDBText>
                )}
                {field.note_text?.trim() && (
                  <VDBText
                    color={theme["core-06"]}
                    type={typography["Minor-R"]}
                  >
                    {t(field.note_text)}
                  </VDBText>
                )}
              </>
            );
        }
      },
    };

    return embedded && (compact || field.type !== "grid") ? (
      <EmbeddedRow compact={compact}>
        <EmbeddedLabel compact={compact}>
          {field.type !== "grid" ? (
            <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
              {`${t(field.label)}${
                field?.validations?.required ? requiredSymbol : ""
              }`}
            </VDBText>
          ) : null}
        </EmbeddedLabel>
        <EmbeddedField>
          <Controller {...commonProps} />
        </EmbeddedField>
      </EmbeddedRow>
    ) : (
      <Controller {...commonProps} />
    );
  };

  if (!filteredFields.length) return null;

  const fields = (
    <InputContainer embedded={embedded} compact={compact}>
      {filteredFields.map((field) => {
        return renderField(field);
      })}
    </InputContainer>
  );

  if (embedded) {
    return <EmbeddedContainer>{fields}</EmbeddedContainer>;
  }

  return (
    <CollapsibleView
      initExpanded={true}
      title={title}
      arrowStyling={{ color: theme["core-04"] }}
      applyShadow
    >
      <Divider />
      {fields}
    </CollapsibleView>
  );
};

const GridContainer = styled.View`
  flex-direction: row;
  justify-content: space-between;
`;

const Divider = styled.View`
  height: 1px;
  flex-direction: row;
  background-color: ${({ theme }) => theme["core-03"]};
  width: 100%;
  max-width: 100%;
`;

const InputContainer = styled.View<{ embedded?: boolean; compact?: boolean }>`
  padding: ${({ embedded }) => (embedded ? "0px" : `${SpaceType.Double}px`)};
  gap: ${({ compact }) => (compact ? "0px" : "10px")};
`;

const EmbeddedContainer = styled.View`
  width: 100%;
`;

const EmbeddedRow = styled.View<{ compact?: boolean }>`
  width: 100%;
  flex-direction: row;
  align-items: ${({ compact }) => (compact ? "flex-start" : "center")};
  padding: ${({ compact }) => (compact ? "8px 16px 0" : "12px 8px")};
`;

const EmbeddedLabel = styled.View<{ compact?: boolean }>`
  width: 35%;
  justify-content: center;
  min-height: ${({ compact }) => (compact ? "32px" : "auto")};
`;

const EmbeddedField = styled.View`
  flex: 1;
`;

const CompactEmbeddedInput = styled.TextInput`
  width: 100%;
  height: 32px;
  min-height: 32px;
  max-height: 32px;
  box-sizing: border-box;
  padding-vertical: 6px;
  padding-horizontal: 8px;
  line-height: 18px;
  border-radius: 8px;
  border-width: 1px;
  border-color: ${({ theme }) => theme["core-04"]};
  color: ${({ theme }) => theme["core-05"]};
  background-color: ${({ theme }) => theme["core-01"]};
  outline-style: none;
`;

const StyledIcon = styled.TouchableOpacity<{ compact?: boolean }>`
  padding: ${({ compact }) =>
    compact ? `${SpaceType.Basic}px` : `${SpaceType.LargeDouble}px`};
`;

export default Personalization;
